<script setup lang="ts">
  import { computed, getCurrentInstance, onMounted, ref } from 'vue';
  import { ClubService } from '@/api/services/club.service';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import V2ClubActivityRow from '@/components/v2/club/V2ClubActivityRow.vue';
  import { useAuth } from '@/composition/use-auth';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { PermissionType } from '@/enums/PermissionType';
  import { IClubActivityItem } from '@/interfaces/IClubActivity';
  import { ClubMemberStatus, IClubMember } from '@/interfaces/IClubMember';
  import { IClubProgram } from '@/interfaces/IClubProgram';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import V2ModalClubAdjust from './V2ModalClubAdjust.vue';
  import V2ModalClubTopup from './V2ModalClubTopup.vue';

  const MODAL_NAME = 'v2-club-member';

  const props = defineProps<{
    memberId: number;
    program: IClubProgram;
  }>();

  const auth = useAuth();
  const modals = useModal();
  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;

  /** Manual adjustments and block/unblock are manager-only (no PIN override). */
  const canManage = computed(() =>
    auth.hasPermission(PermissionType.ClubManage)
  );

  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const symbol = computed(() => restaurantStore.currency?.symbol ?? '$');

  //#region Member

  const member = ref<IClubMember | null>(null);
  const loading = ref(true);

  const isBlocked = computed(
    () => member.value?.status === ClubMemberStatus.Blocked
  );

  onMounted(async () => {
    try {
      member.value = await ClubService.getMember(props.memberId);
      loading.value = false;
      await loadMoreActivity();
    } catch (e) {
      ui.serverError(e, i18nT('club.member.loadError'));
      // The modal unmounts here; don't touch reactive state afterwards.
      modals.hide(MODAL_NAME);
    }
  });

  /** Pushes fresh member data up to the list and into local state. */
  function applyUpdated(updated: IClubMember) {
    member.value = updated;
    proxy.$parent?.$emit('updated', updated);
  }

  //#endregion

  //#region Activity

  const activity = ref<IClubActivityItem[]>([]);
  const nextCursor = ref<string | null>(null);
  const activityLoaded = ref(false);
  const activityLoading = ref(false);
  /** Audit filter: only manual staff adjustments (server-side, keeps paging). */
  const adjustOnly = ref(false);

  async function loadMoreActivity() {
    if (activityLoading.value) return;
    if (activityLoaded.value && !nextCursor.value) return;

    try {
      activityLoading.value = true;
      const page = await ClubService.getMemberActivity(
        props.memberId,
        activityLoaded.value ? nextCursor.value : null,
        adjustOnly.value ? 'adjust' : undefined
      );
      activity.value.push(...page.items);
      nextCursor.value = page.nextCursor;
      activityLoaded.value = true;
    } catch (e) {
      ui.serverError(e, i18nT('club.member.activityError'));
    } finally {
      activityLoading.value = false;
    }
  }

  async function reloadActivity() {
    activity.value = [];
    nextCursor.value = null;
    activityLoaded.value = false;
    await loadMoreActivity();
  }

  async function toggleAdjustFilter() {
    if (activityLoading.value) return;
    adjustOnly.value = !adjustOnly.value;
    await reloadActivity();
  }

  function formatDate(value: string | null): string {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  //#endregion

  //#region Actions

  function openAdjust(mode: 'points' | 'cash') {
    if (!member.value) return;

    modals.show(
      V2ModalClubAdjust,
      { member: member.value, mode },
      {
        name: 'v2-club-adjust',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        width: 480,
        maxWidth: Math.round(window.innerWidth * 0.95),
        clickToClose: false,
      },
      {
        adjusted: (updated: IClubMember) => {
          applyUpdated(updated);
          reloadActivity();
        },
      }
    );
  }

  function openTopup() {
    if (!member.value) return;

    modals.show(
      V2ModalClubTopup,
      { member: member.value, program: props.program },
      {
        name: 'v2-club-topup',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        width: 480,
        maxWidth: Math.round(window.innerWidth * 0.95),
        clickToClose: false,
      },
      {
        'topped-up': (updated: IClubMember) => {
          applyUpdated(updated);
          reloadActivity();
        },
      }
    );
  }

  const togglingBlock = ref(false);

  async function toggleBlock() {
    if (!member.value || togglingBlock.value) return;

    const blocking = !isBlocked.value;
    const confirmed = await modals.confirm({
      title: i18nT(
        blocking ? 'club.member.blockTitle' : 'club.member.unblockTitle'
      ),
      message: i18nT(
        blocking ? 'club.member.blockMessage' : 'club.member.unblockMessage',
        { name: member.value.name }
      ),
      acceptText: i18nT(
        blocking ? 'club.member.blockCta' : 'club.member.unblockCta'
      ),
      cancelText: i18nT('cancel'),
      acceptColor: blocking ? 'error' : undefined,
    });
    if (!confirmed) return;

    try {
      togglingBlock.value = true;
      const updated = blocking
        ? await ClubService.blockMember(member.value.id)
        : await ClubService.unblockMember(member.value.id);
      applyUpdated(updated);
    } catch (e) {
      ui.serverError(e, i18nT('club.member.blockError'));
    } finally {
      togglingBlock.value = false;
    }
  }

  //#endregion

  function close() {
    modals.hide(MODAL_NAME);
  }
</script>

<template>
  <div class="v2-modal-club-member">
    <div class="modal-header">
      <template v-if="member">
        <div class="header-identity">
          <span class="header-name">{{ member.name }}</span>
          <span class="header-number">
            {{ $t('club.member.number', { number: member.memberNumber }) }}
          </span>
          <PosUiBadge
            :color="isBlocked ? 'error' : 'success'"
            variant="soft"
            size="sm"
          >
            {{
              isBlocked
                ? $t('club.member.statusBlocked')
                : $t('club.member.statusActive')
            }}
          </PosUiBadge>
        </div>
      </template>
      <span v-else class="header-name">{{ $t('club.member.title') }}</span>

      <button class="header-close" type="button" @click="close">
        <PosUiIcon name="close" :size="22" />
      </button>
    </div>

    <div class="modal-content">
      <template v-if="loading">
        <PosUiSkeleton class="skeleton-block" />
        <PosUiSkeleton class="skeleton-block" />
      </template>

      <template v-else-if="member">
        <!-- Balances -->
        <div class="balance-cards">
          <div class="balance-card">
            <span class="balance-card-label">
              {{ $t('club.member.points') }}
            </span>
            <span class="balance-card-value">
              {{ member.pointsBalance.toLocaleString('es-AR') }}
            </span>
          </div>
          <div v-if="program.balanceEnabled" class="balance-card">
            <span class="balance-card-label">
              {{ $t('club.member.balance') }}
            </span>
            <span class="balance-card-value">
              {{ symbol }}{{ member.cashTotal.toLocaleString('es-AR') }}
            </span>
            <span v-if="member.cashBonus > 0" class="balance-card-detail">
              {{
                $t('club.member.balanceSplit', {
                  symbol,
                  principal: member.cashPrincipal.toLocaleString('es-AR'),
                  bonus: member.cashBonus.toLocaleString('es-AR'),
                })
              }}
            </span>
          </div>
        </div>

        <!-- Profile -->
        <div class="profile-grid">
          <div class="profile-item">
            <span class="profile-label">{{ $t('club.member.phone') }}</span>
            <span class="profile-value">{{ member.phone || '—' }}</span>
          </div>
          <div class="profile-item">
            <span class="profile-label">{{ $t('club.member.email') }}</span>
            <span class="profile-value">{{ member.email || '—' }}</span>
          </div>
          <div class="profile-item">
            <span class="profile-label">{{ $t('club.member.joinedAt') }}</span>
            <span class="profile-value">{{
              formatDate(member.createdAt)
            }}</span>
          </div>
          <div class="profile-item">
            <span class="profile-label">{{ $t('club.member.lastVisit') }}</span>
            <span class="profile-value">
              {{ formatDate(member.lastVisitAt) }}
            </span>
          </div>
          <div class="profile-item">
            <span class="profile-label">{{ $t('club.member.visits') }}</span>
            <span class="profile-value">{{ member.visitsCount }}</span>
          </div>
          <div class="profile-item">
            <span class="profile-label">
              {{ $t('club.member.lifetimeSpend') }}
            </span>
            <span class="profile-value">
              {{ symbol }}{{ member.lifetimeSpend.toLocaleString('es-AR') }}
            </span>
          </div>
        </div>

        <!-- Actions -->
        <div class="member-actions">
          <PosUiButton
            v-if="canManage"
            color="neutral"
            variant="outline"
            size="sm"
            icon="star-plus-outline"
            @click="openAdjust('points')"
          >
            {{ $t('club.member.adjustPoints') }}
          </PosUiButton>
          <PosUiButton
            v-if="canManage && program.balanceEnabled"
            color="neutral"
            variant="outline"
            size="sm"
            icon="cash-plus"
            @click="openAdjust('cash')"
          >
            {{ $t('club.member.adjustBalance') }}
          </PosUiButton>
          <PosUiButton
            v-if="program.balanceEnabled && !isBlocked"
            color="neutral"
            variant="outline"
            size="sm"
            icon="wallet-plus-outline"
            @click="openTopup"
          >
            {{ $t('club.member.topup') }}
          </PosUiButton>
          <PosUiButton
            v-if="canManage"
            :color="isBlocked ? 'success' : 'error'"
            variant="soft"
            size="sm"
            :icon="
              isBlocked ? 'account-check-outline' : 'account-cancel-outline'
            "
            :loading="togglingBlock"
            @click="toggleBlock"
          >
            {{
              isBlocked
                ? $t('club.member.unblockCta')
                : $t('club.member.blockCta')
            }}
          </PosUiButton>
        </div>

        <!-- Activity -->
        <div class="activity-section">
          <div class="activity-header">
            <h4 class="activity-title">{{ $t('club.member.activity') }}</h4>
            <PosUiButton
              :color="adjustOnly ? 'info' : 'neutral'"
              :variant="adjustOnly ? 'soft' : 'outline'"
              size="xs"
              icon="tune-variant"
              @click="toggleAdjustFilter"
            >
              {{ $t('club.member.filterAdjustments') }}
            </PosUiButton>
          </div>

          <div
            v-if="activityLoaded && activity.length === 0"
            class="activity-empty"
          >
            {{
              adjustOnly
                ? $t('club.member.activityEmptyAdjustments')
                : $t('club.member.activityEmpty')
            }}
          </div>

          <div v-else class="activity-list">
            <V2ClubActivityRow
              v-for="item in activity"
              :key="`${item.source}-${item.id}`"
              :item="item"
            />

            <PosUiButton
              v-if="nextCursor"
              color="neutral"
              variant="ghost"
              size="sm"
              block
              :loading="activityLoading"
              @click="loadMoreActivity"
            >
              {{ $t('club.member.activityMore') }}
            </PosUiButton>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-modal-club-member {
    @apply tw-flex tw-max-h-full tw-min-h-0 tw-w-full tw-flex-col tw-bg-white;

    .modal-header {
      @apply tw-flex tw-flex-none tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-neutral-200 tw-px-5 tw-py-4;

      .header-identity {
        @apply tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-2;
      }

      .header-name {
        @apply tw-truncate tw-text-lg tw-font-semibold tw-text-neutral-900;
      }

      .header-number {
        @apply tw-text-sm tw-tabular-nums tw-text-neutral-500;
      }

      .header-close {
        @apply tw-flex tw-h-9 tw-w-9 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-text-neutral-400 tw-transition-colors hover:tw-bg-neutral-100 hover:tw-text-neutral-700;
      }
    }

    .modal-content {
      @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-4 tw-overflow-y-auto tw-px-5 tw-py-4;

      .skeleton-block {
        @apply tw-h-20 tw-w-full tw-rounded-xl;
      }

      .balance-cards {
        @apply tw-grid tw-grid-cols-2 tw-gap-3;

        .balance-card {
          @apply tw-flex tw-flex-col tw-gap-0.5 tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-p-3;

          .balance-card-label {
            @apply tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-neutral-500;
          }

          .balance-card-value {
            @apply tw-text-2xl tw-font-semibold tw-tabular-nums tw-text-neutral-900;
          }

          .balance-card-detail {
            @apply tw-text-xs tw-tabular-nums tw-text-neutral-500;
          }
        }
      }

      .profile-grid {
        @apply tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-2 sm:tw-grid-cols-3;

        .profile-item {
          @apply tw-flex tw-min-w-0 tw-flex-col;

          .profile-label {
            @apply tw-text-xs tw-text-neutral-400;
          }

          .profile-value {
            @apply tw-truncate tw-text-sm tw-text-neutral-800;
          }
        }
      }

      .member-actions {
        @apply tw-flex tw-flex-wrap tw-gap-2;
      }

      .activity-section {
        @apply tw-flex tw-flex-col tw-gap-2;

        .activity-header {
          @apply tw-flex tw-items-center tw-justify-between tw-gap-2;
        }

        .activity-title {
          @apply tw-m-0 tw-text-sm tw-font-semibold tw-text-neutral-900;
        }

        .activity-empty {
          @apply tw-rounded-lg tw-bg-neutral-50 tw-px-3 tw-py-4 tw-text-center tw-text-sm tw-text-neutral-500;
        }

        .activity-list {
          @apply tw-flex tw-flex-col;
        }
      }
    }
  }
</style>
